import { OrganizationYearWise } from 'models/CompanySetup/organisationYearWise.model';
import { Organization } from 'models/CompanySetup/organization.model';
import { MetaData } from 'models/MetaData/metaData.model';

export enum EditPageModals {
  RESET_MODAL = 'reset',
  UNSAVED_CHANGES_MODAL = 'unsaved',
}

export const handleParentChildHeirarchy = (
  organisationList: Organization[],
  selectedOrganisation: Organization
): Organization[] => {
  const { organisationYearWise: selectedOrganisationYearWise } =
    selectedOrganisation;
  // Check if the selectedOrganisation exists in the list
  const isOrganisationInList = organisationList.some(
    organisation => organisation.name === selectedOrganisation.name
  );

  // If the selectedOrganisation is not in the list, add it
  if (!isOrganisationInList) {
    organisationList.push(selectedOrganisation);
    return organisationList;
  }

  return organisationList.map(organisation => {
    if (
      !!organisation?.organisationYearWise?.parentName &&
      organisation?.organisationYearWise?.parentName ===
        selectedOrganisation.name
    ) {
      //Update selectedOrganisation's children's parent as selectedOrganisation's parent
      return {
        ...organisation,
        organisationYearWise: {
          ...organisation.organisationYearWise,
          parentName: selectedOrganisationYearWise?.parentName,
        },
      };
    }
    return organisation;
  });
};

export const handleAddParent = (
  organisationList: Organization[],
  currentOrganisation: Organization,
  selectedOrganisation: Organization
): Organization[] => {
  // Step 1: Update children of the selectedOrganisation
  let updatedOrganisationList = handleParentChildHeirarchy(
    organisationList,
    selectedOrganisation
  );
  // Step 2: Update the parent-child relationships
  updatedOrganisationList = updatedOrganisationList.map(organisation => {
    const isNotMainParent =
      !!currentOrganisation.organisationYearWise?.parentName;
    if (organisation.name === currentOrganisation.name) {
      // Update currentOrganisation's parent to be selectedOrganisation
      return {
        ...organisation,
        organisationYearWise: {
          ...organisation.organisationYearWise,
          //Updating organisation share of current organisation to undefined if current organisation is the main parent
          organizationShare: isNotMainParent
            ? organisation.organisationYearWise?.organizationShare
            : undefined,
          parentName: selectedOrganisation.name,
          //Updating isParent of current organisation to false if current organisation is the main parent
          isParent: isNotMainParent
            ? organisation.organisationYearWise?.isParent
            : false,
        },
        //Removing company details so that if user adds more than one new main parent, data remains proper
        company: isNotMainParent ? organisation.company : undefined,
      };
    }
    if (organisation.name === selectedOrganisation.name) {
      if (isNotMainParent) {
        // Update selectedOrganisation's parent to be currentOrganisation's parent
        return {
          ...organisation,
          organisationYearWise: {
            ...organisation.organisationYearWise,
            parentName: currentOrganisation?.organisationYearWise?.parentName,
          },
        };
      } else {
        //Update selectedOrganisation as the main parent
        return {
          ...organisation,
          organisationYearWise: {
            ...organisation.organisationYearWise,
            parentName: null,
            organizationShare: 100,
            isParent: true,
          },
        };
      }
    }
    return organisation as Organization;
  });

  return organisationSorter(updatedOrganisationList);
};

export const handleAddChild = (
  organisationList: Organization[],
  currentOrganisation: Organization,
  selectedOrganisation: Organization
): Organization[] => {
  // Step 1: Update children of the selectedOrganisation
  let updatedOrganisationList = handleParentChildHeirarchy(
    organisationList,
    selectedOrganisation
  );

  // Step 2: Update the parent-child relationships
  updatedOrganisationList = updatedOrganisationList.map(organisation => {
    if (organisation.name === selectedOrganisation.name) {
      // Update selectedOrganisation's parent to be currentOrganisation
      return {
        ...organisation,
        organisationYearWise: {
          ...organisation.organisationYearWise,
          parentName: currentOrganisation.name,
        },
      };
    }
    return organisation as Organization;
  });

  return organisationSorter(updatedOrganisationList);
};

export const organisationSorter = (
  organisationList: Organization[]
): Organization[] => {
  return organisationList.sort((a, b) => {
    if (!a.organisationYearWise?.parentName) return -1; // a is the main parent, should be first
    if (!b.organisationYearWise?.parentName) return 1; // b is the main parent, should be first
    return 0; // keep the original order for the rest
  });
};

export const updateOrganisationList = (
  organisationList: MetaData[],
  organisationListData: Organization[]
): MetaData[] => {
  const updatedOrganisationList: MetaData[] = [];
  const nameToOrgMap = new Map<string, MetaData>();

  // Populate map with existing organisationList
  organisationList.forEach(org => {
    nameToOrgMap.set(org.name ?? '', org);
  });

  // Update the organisationList based on organisationListData
  organisationListData.forEach(orgData => {
    const existingOrg = nameToOrgMap.get(orgData.name ?? '');
    if (existingOrg) {
      // If the organisation exists in the list, retain or update it
      updatedOrganisationList.push(existingOrg);
    } else {
      // Add new organization from organisationListData
      updatedOrganisationList.push({ name: orgData.name });
    }
    // Remove processed organizations from the map
    nameToOrgMap.delete(orgData.name ?? '');
  });

  // Add organizations that have an ID but are not present in organisationListData
  nameToOrgMap.forEach(org => {
    if (!!org.id) {
      updatedOrganisationList.push(org);
    }
  });

  return updatedOrganisationList;
};

export const generateNewOrganisationData = (
  organisationListMeta: MetaData[],
  organisationName: string
) => {
  const organisation = organisationListMeta.find(
    item => item.name === organisationName
  );
  return {
    ...new Organization(),
    id: organisation?.id,
    name: organisationName,
    organisationYearWise: {
      ...new OrganizationYearWise(),
      isActive: true,
      organizationShare: 100,
    },
  };
};
